import React from "react";
import { Badge } from "antd";

import keyboardJS from "keyboardjs";
import { common } from "../../../utils/common";

import _ from "lodash";

import { translateKey as t } from "../../../locales/translation";

export class CrudCommon {
  private controller: any;
  private tableDataName: string = "";
  private os: string = common.getOS();
  private multiSelectKey: string = this.os === "macOS" ? "command" : "ctrl";

  constructor(controller, tableDataName) {
    this.controller = controller;
    this.tableDataName = tableDataName;
  }

  public enableKeyboardShortcuts = () => {
    // Select multiple rows through the key combination ctrl/command + mouse click
    keyboardJS.on(
      this.multiSelectKey,
      () => {
        this.controller.multiSelectKeyPressed = true;
      },
      () => {
        this.controller.multiSelectKeyPressed = false;
      }
    );

    // Select by blocks through the key combination shift + mouse click
    keyboardJS.on(
      "shift",
      () => {
        this.controller.shiftKeyPressed = true;
      },
      () => {
        this.controller.shiftKeyPressed = false;
      }
    );

    // Close the form or the cells editing state through the esc key
    keyboardJS.bind("escape", () => {
      const { tableInstance } = this.controller;

      if (this.controller.isFormModalVisible) {
        let cancelBtn: HTMLElement = document.querySelector('button[data-e2e="form-modal-cancel-btn"]');
        cancelBtn.click();
      } else {
        tableInstance.handleCellEdit();
      }

      if (tableInstance.multiSelectedRows.totSelected > 0 || tableInstance.selectedRow.row) {
        tableInstance.resetMultiselectedRows();
        tableInstance.resetSelectedRow();
      }
    });

    // Select the table row above the currently selected one, through the key combination alt + w
    keyboardJS.bind("alt + w", () => {
      const { tableInstance } = this.controller;

      let previouslyRowIndex: number =
        this.controller[this.tableDataName].findIndex((d) => d._id === tableInstance.selectedRow.row) - 1;

      if (previouslyRowIndex >= 0) {
        tableInstance.selectedRow = {
          fromTable: true,
          row: this.controller[this.tableDataName][previouslyRowIndex]._id,
          rowData: this.controller[this.tableDataName][previouslyRowIndex],
        };
      } else {
        tableInstance.selectedRow = {
          fromTable: true,
          row: this.controller[this.tableDataName][this.controller[this.tableDataName].length - 1]?._id,
          rowData: this.controller[this.tableDataName][this.controller[this.tableDataName].length - 1],
        };
      }

      //tableInstance.scrollToRow = previouslyRowIndex + 5

      tableInstance.handleCellEdit();
    });

    // Select the table row below the currently selected one, through the key combination alt + s
    keyboardJS.bind("alt + s", () => {
      const { tableInstance } = this.controller;

      let nextRowIndex: number =
        this.controller[this.tableDataName].findIndex((d) => d._id === tableInstance.selectedRow.row) + 1;

      if (nextRowIndex < this.controller[this.tableDataName].length) {
        tableInstance.selectedRow = {
          fromTable: true,
          row: this.controller[this.tableDataName][nextRowIndex]._id,
          rowData: this.controller[this.tableDataName][nextRowIndex],
        };
      } else {
        tableInstance.selectedRow = {
          fromTable: true,
          row: this.controller[this.tableDataName][0]?._id,
          rowData: this.controller[this.tableDataName][0],
        };
      }

      //tableInstance.scrollToRow = nextRowIndex - 5

      tableInstance.handleCellEdit();
    });

    // Temporarily removed because it conflicts with other commands
    // Open the edit form, for the selected row, through the enter key
    // keyboardJS.bind('enter', () => {
    //   const { isFormModalVisible, deleteModal, isDiscardChangesModalVisible, saveModal } = this.controller
    //   const selectedRow = this.controller.tableInstance.selectedRow.rowData

    //   if (
    //     selectedRow &&
    //     !isFormModalVisible &&
    //     !deleteModal.visible &&
    //     !isDiscardChangesModalVisible &&
    //     !saveModal?.visible
    //   ) {
    //     this.controller.openEditForm(selectedRow)
    //   }
    // })

    // Open the create form
    keyboardJS.bind("alt + n", () => {
      if (!this.controller.isFormModalVisible) {
        let addBtn: HTMLElement = document.querySelector('button[data-e2e="table-buttons-add-btn"]');
        addBtn?.click();
      }
    });

    // Open the delete modal for the selected rows
    keyboardJS.bind("alt + r", () => {
      if (!this.controller.deleteModal?.visible) {
        let deleteBtn: HTMLElement = document.querySelector('button[data-e2e="table-buttons-delete-btn"]');
        deleteBtn?.click();
      }
    });

    // Open the discard changes modal
    keyboardJS.bind("alt + b", () => {
      if (this.controller.modifiedRows?.length !== 0) {
        let discardChangesBtn: HTMLElement = document.querySelector('button[data-e2e="table-buttons-discard-btn"]');
        discardChangesBtn?.click();
      }
    });

    // Open the save changes modal
    keyboardJS.bind("alt + enter", () => {
      if (this.controller.modifiedRows?.length !== 0 && !this.controller.isDiscardChangesModalVisible) {
        let saveTableBtn: HTMLElement = document.querySelector('button[data-e2e="table-buttons-save-btn"]');
        saveTableBtn?.click();
      }
    });

    // Open the analytics section
    keyboardJS.bind("alt + g", () => {
      let analyticsBtn: HTMLElement = document.querySelector('button[data-e2e="analytics-section-btn"]');
      analyticsBtn?.click();
    });

    // Show additional columns
    keyboardJS.bind("alt + =", () => {
      if (this.controller.additionalColumnsAreVisible !== undefined && !this.controller.additionalColumnsAreVisible) {
        this.controller.setAdditionalColumnsAreVisible(true);
      }
    });

    // Hide additional columns
    keyboardJS.bind("alt + -", () => {
      if (this.controller.additionalColumnsAreVisible !== undefined && this.controller.additionalColumnsAreVisible) {
        this.controller.setAdditionalColumnsAreVisible(false);
      }
    });

    // Switch between table view modes
    keyboardJS.bind("alt + 1", () => {
      this.controller.tableInstance.viewModeChange("compact");
      this.controller.tableInstance.viewModeChange("compact");
    });
    keyboardJS.bind("alt + 2", () => {
      this.controller.tableInstance.viewModeChange("default");
      this.controller.tableInstance.viewModeChange("default");
    });
    keyboardJS.bind("alt + 3", () => {
      this.controller.tableInstance.viewModeChange("detailed");
      this.controller.tableInstance.viewModeChange("detailed");
    });
  };

  public handleRowClick = (row: any) => {
    // When the user clicks a row other than the one previously selected, closes any eventual cell's editing state
    if (this.controller.clickedRow._id !== row._id) {
      this.controller.tableInstance.handleCellEdit();
      this.controller.clickedRow = row;
    }
  };

  public handleMultiselection = () => {
    this.controller.tableInstance.handleCellEdit();
  };

  public handleDiscardChanges = () => {
    this.controller.modifiedRows.forEach((modRow) => {
      // Lookups item by modified field _id
      const foundItem: any = this.controller[this.tableDataName].find(
        (item) => modRow.filter && modRow.filter._id === item._id
      );

      // Gets modified fields
      const modifiedDataFields: any = Object.keys(modRow.data);

      // Loops through modified fields and applies original values
      modifiedDataFields.forEach((accessor) => {
        const originalValue: any = _.get(modRow.original, accessor, "");
        _.set(foundItem, accessor, originalValue);
      });
    });

    this.controller.modifiedRows.length = 0;
  };

  public renderTableInfo = () => {
    const keyStyle: any = {
      backgroundColor: "#eee",
      borderRadius: "3px",
      border: "1px solid #b4b4b4",
      boxShadow: "0 1px 1px rgba(0, 0, 0, .2), 0 2px 0 0 rgba(255, 255, 255, .7) inset",
      color: "#333",
      display: "inline-block",
      fontSize: ".85em",
      fontWeight: 700,
      lineHeight: 1,
      padding: "2px 4px",
      whiteSpace: "nowrap",
    };

    const tableInfo: Array<any> = [
      <strong key={0} style={{ display: "block", marginTop: -10 }}>
        {t("lblGeneral", "General")}
      </strong>,
      // Temporarily removed because we disabled the feature everywhere
      // <Badge status='default' text={t('lblToEditTheCellDoubleClickOnIt', 'To edit the cell - double click on it')} key={1} />,
      <Badge
        status="default"
        text={
          <span>
            <span>
              {t(
                "lblSelectMultipleRowsByCheckingCheckboxesOrUsingKeyCombination",
                "Select multiple rows either by checking the checkboxes or using the key combination"
              )}
            </span>
            <kbd style={{ ...keyStyle, ...{ marginLeft: "5px" } }}>{this.multiSelectKey}</kbd>
            {" + "}
            <kbd style={{ fontSize: ".9em", fontWeight: 700, color: "#333" }}>{t("lblMouseClick", "mouse click")}</kbd>
          </span>
        }
        key={2}
      />,
      <Badge
        status="default"
        text={
          <span>
            <span>{t("lblSelectByBlocksUsingTheKeyCombination", "Select by blocks using the key combination")}</span>
            <kbd style={{ ...keyStyle, ...{ marginLeft: "5px" } }}>{t("lblShift", "shift")}</kbd>
            {" + "}
            <kbd style={{ fontSize: ".9em", fontWeight: 700, color: "#333" }}>{t("lblMouseClick", "mouse click")}</kbd>
          </span>
        }
        key={3}
      />,
      <Badge
        status="default"
        text={
          <span>
            {t(
              "lblOpenTheContextMenuByRightClickingInTheTable",
              "Open the context menu by right-clicking in the table"
            )}
          </span>
        }
        key={4}
      />,
      <strong key={5} style={{ display: "block", marginTop: 5 }}>
        {t("lblKeyboardShortcuts", "Keyboard shortcuts")}
      </strong>,
      <Badge
        status="default"
        text={
          <span>
            <span>{t("lblSelectRowsUsingTheKeyCombinations", "Select the rows using the key combinations")}</span>
            <kbd style={{ ...keyStyle, ...{ marginLeft: "5px" } }}>alt</kbd>
            {"+"}
            <kbd style={{ ...keyStyle, ...{ marginRight: "5px" } }}>w</kbd>
            {"&"}
            <kbd style={{ ...keyStyle, ...{ marginLeft: "5px" } }}>alt</kbd>
            {"+"}
            <kbd style={{ ...keyStyle, ...{ marginRight: "5px" } }}>s</kbd>
          </span>
        }
        key={6}
      />,
      // Apparently, these shortcuts no longer work
      // <Badge
      //   status='default'
      //   text={
      //     <span>
      //       {t('lblScrollTheTableUsingTheKeys', 'Scroll the table using the keys')}
      //       <kbd style={{ ...keyStyle, ...{ marginLeft: '5px', marginRight: '5px' } }}>{t('lblUp', 'up')}</kbd>
      //       {'&'}
      //       <kbd style={{ ...keyStyle, ...{ marginLeft: '5px' } }}>{t('lblDown', 'down')}</kbd>
      //     </span>
      //   }
      //   key={7}
      // />,
      // Apparently, this shortcut no longer work
      // <Badge
      //   status='default'
      //   text={
      //     <span>
      //       {t('lblScrollDownABlockOfRowsUsingTheKey', 'Scroll down a block of rows using the key')}
      //       <kbd style={{ ...keyStyle, ...{ marginLeft: '5px' } }}>{t('lblSpace', 'space')}</kbd>
      //     </span>
      //   }
      //   key={8}
      // />,
      // This shorcut is temporarily removed because it conflicts with other commands
      // <Badge
      //   status='default'
      //   text={
      //     <span>
      //       {t('lblOpenTheEditFormUsingTheKey', 'Open the edit form, for the selected row, using the key')}
      //       <kbd style={{ ...keyStyle, ...{ marginLeft: '5px' } }}>{t('lblEnter', 'enter')}</kbd>
      //     </span>
      //   }
      //   key={9}
      // />,
      <Badge
        status="default"
        style={{ lineHeight: 0 }}
        text={
          <span>
            {t(
              "lblSwitchBetweenTableViewModesUsingTheKeyCombinations",
              "Switch between table view modes using the key combinations:"
            )}
            <kbd style={{ ...keyStyle, ...{ marginLeft: "5px" } }}>alt</kbd>
            {"+"}
            <kbd style={keyStyle}>1</kbd>
            <span style={{ marginLeft: "1px", fontSize: "1.5em" }}>,</span>

            <kbd style={{ ...keyStyle, ...{ marginLeft: "5px" } }}>alt</kbd>
            {"+"}
            <kbd style={keyStyle}>2</kbd>
            <span style={{ marginLeft: "1px", fontSize: "1.5em" }}>,</span>

            <kbd style={{ ...keyStyle, ...{ marginLeft: "5px" } }}>alt</kbd>
            {"+"}
            <kbd style={keyStyle}>3</kbd>
          </span>
        }
        key={10}
      />,
      <Badge
        status="default"
        text={
          <span>
            {t(
              "lblHoverOnTheMainTableButtonsToSeeTheirKeyboardShortcut",
              "Move the mouse cursor over the main table buttons to see their keyboard shortcut"
            )}
          </span>
        }
        key={11}
      />,
    ];

    return tableInfo;
  };
}
